import React from 'react';
import { Scale, Users, Building2, Briefcase, Globe, GraduationCap, Check } from 'lucide-react';

// We'll create a Card component directly here to ensure design consistency
const Card = ({ children, className }) => {
  return (
    <div className={`bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-8 ${className}`}>
      {children}
    </div>
  );
};

const PracticeAreas = () => {
    const areas = [
        {
            icon: <Users className="w-12 h-12" />,
            title: 'Family Law and Court Marriage',
            description: 'Compassionate guidance through family matters, ensuring the best outcomes for all parties involved.',
            features: ['Divorce', 'Child Custody', 'Alimony', 'Marriage Contracts'],
            color: 'text-blue-600',
            bgColor: 'bg-blue-50'
        },
        {
            icon: <Scale className="w-12 h-12" />,
            title: 'Criminal Law',
            description: 'Expert defense in criminal cases, protecting individual rights and ensuring fair representation.',
            features: ['Criminal Defense', 'White Collar Crimes', 'Appeals', 'Bail Applications'],
            color: 'text-red-600',
            bgColor: 'bg-red-50'
        },
        {
            icon: <Building2 className="w-12 h-12" />,
            title: 'Corporate Law',
            description: 'Comprehensive legal solutions for businesses, from startups to established corporations.',
            features: ['Company Formation', 'Contract Review', 'Mergers & Acquisitions', 'Corporate Compliance'],
            color: 'text-green-600',
            bgColor: 'bg-green-50'
        },
        {
            icon: <Briefcase className="w-12 h-12" />,
            title: 'Civil Law',
            description: 'Skilled representation in civil matters, protecting your rights and interests in legal disputes.',
            features: ['Property Disputes', 'Contract Disputes', 'Civil Litigation', 'Debt Recovery'],
            color: 'text-purple-600',
            bgColor: 'bg-purple-50'
        },
        {
            icon: <Globe className="w-12 h-12" />,
            title: 'International Law',
            description: 'Expert guidance in international legal matters and cross-border transactions.',
            features: ['International Trade', 'Foreign Investment', 'Cross-border Disputes', 'Immigration'],
            color: 'text-indigo-600',
            bgColor: 'bg-indigo-50'
        },
        {
            icon: <GraduationCap className="w-12 h-12" />,
            title: 'Excise and Tax',
            description: 'Protecting employee rights and helping employers maintain compliant workplaces.',
            features: ['Employment Contracts', 'Workplace Disputes', 'Benefits & Compensation', 'Labor Compliance'],
            color: 'text-amber-600',
            bgColor: 'bg-amber-50'
        }
    ];

    return (
        <div>
            {/* Hero Section */}
            <div className="relative h-[400px] bg-gray-900">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-900 to-indigo-700 opacity-90"></div>
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptNiA2djZoNnYtNmgtNnptLTEyIDBoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnoiLz48cGF0aCBkPSJNMTIgMTJoNnY2aC02di02em02IDZoNnY2aC02di02em0wLTZoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnptLTEyIDEyaDZ2NmgtNnYtNnptMTIgMGg2djZoLTZ2LTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
                <div className="relative container mx-auto px-4 h-full flex items-center">
                    <div>
                        <div className="inline-block bg-white/20 text-white px-4 py-1 rounded-md text-sm font-medium mb-4 backdrop-blur-sm">
                            LEGAL EXPERTISE
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Practice Areas</h1>
                        <p className="text-gray-100 max-w-xl text-lg">
                            Our team provides expert legal representation across a diverse range of practice areas.
                        </p>
                    </div>
                </div>
            </div>

            {/* Practice Areas Content */}
            <div className="container mx-auto px-4 py-20">
                <div className="mb-12 text-center">
                    <h2 className="text-3xl font-bold mb-4">Specialized Legal Services</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        We offer comprehensive legal solutions tailored to your specific needs with a focus on excellence and results.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {areas.map((area, index) => (
                        <div key={index} className="group">
                            <Card className="flex flex-col h-full group-hover:border-l-4 group-hover:border-indigo-600 transition-all">
                                <div className={`${area.bgColor} ${area.color} p-4 inline-flex rounded-lg mb-6 transition-colors duration-300`}>
                                    {area.icon}
                                </div>
                                <h2 className="text-2xl font-bold mb-4 group-hover:text-indigo-700 transition-colors">{area.title}</h2>
                                <p className="text-gray-600 mb-6">{area.description}</p>
                                <div className="mt-auto">
                                    <h3 className="font-semibold text-gray-800 mb-3">We provide:</h3>
                                    <ul className="space-y-3">
                                        {area.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-center text-gray-600">
                                                <div className={`w-6 h-6 flex items-center justify-center rounded-full mr-3 ${area.bgColor}`}>
                                                    <Check className={`w-4 h-4 ${area.color}`} />
                                                </div>
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>

            {/* Why Choose Us Section */}
            <div className="bg-gray-50 py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-block bg-indigo-100 text-indigo-800 px-4 py-1 rounded-md text-sm font-medium mb-4">
                            WHY CHOOSE US
                        </div>
                        <h2 className="text-3xl font-bold mb-6">Expert Legal Representation You Can Trust</h2>
                        <p className="text-gray-600 mb-10">
                            Our team of experienced attorneys is dedicated to providing high-quality legal services with a client-centered approach. We combine legal expertise with compassionate service to achieve the best possible outcomes for our clients.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <div className="text-indigo-600 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold mb-2">Experienced Team</h3>
                                <p className="text-gray-600">With years of experience in diverse legal areas, our attorneys bring deep expertise to every case.</p>
                            </div>
                            
                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <div className="text-indigo-600 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold mb-2">Quick Response</h3>
                                <p className="text-gray-600">We understand that legal issues often require immediate attention and we pride ourselves on our responsiveness.</p>
                            </div>
                            
                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <div className="text-indigo-600 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold mb-2">Client-Centered</h3>
                                <p className="text-gray-600">We prioritize our clients' needs and interests, providing personalized legal solutions for each case.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PracticeAreas;